import { useI18n } from '@/hooks/useI18n';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { IdentityResponse } from '@/types/api';
import { useCreateIdentity, useUpdateIdentity } from '@/hooks/mutations/useIdentityCrud';

const schema = z.object({
  name: z.string().min(1, 'required').max(64, 'max64'),
  description: z.string().max(240, 'max240').optional(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'invalidColor')
    .optional()
    .or(z.literal('')),
});

type Values = z.infer<typeof schema>;

const PALETTE = [
  '#22d3ee',
  '#a78bfa',
  '#34d399',
  '#fbbf24',
  '#fb7185',
  '#60a5fa',
  '#e879f9',
  '#f97316',
];

interface IdentityUpsertDialogProps {
  identity: IdentityResponse | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function IdentityUpsertDialog({ identity, open, onOpenChange }: IdentityUpsertDialogProps) {
  const { t } = useI18n();
  const create = useCreateIdentity();
  const update = useUpdateIdentity();
  const editing = !!identity;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      color: '#a78bfa',
    },
  });

  const color = watch('color') ?? '#a78bfa';

  useEffect(() => {
    if (open) {
      reset({
        name: identity?.name ?? '',
        description: identity?.description ?? '',
        color: identity?.color ?? '#a78bfa',
      });
    }
  }, [open, identity, reset]);

  const onSubmit = (v: Values) => {
    const body = {
      name: v.name,
      description: v.description || null,
      color: v.color || null,
    };
    if (editing) {
      update.mutate({ id: identity.id, body }, { onSuccess: () => onOpenChange(false) });
    } else {
      create.mutate(body, { onSuccess: () => onOpenChange(false) });
    }
  };

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editing
              ? t('Modifier l’identité', 'Edit identity')
              : t('Nouvelle identité', 'New identity')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'Regroupez vos comptes par thème pour suivre leur performance.',
              'Group your accounts by theme to track their performance.',
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Field label={t('Nom', 'Name')} error={errors.name?.message}>
            <Input
              autoFocus
              placeholder={t('fitness, beauté, cuisine…', 'fitness, beauty, cooking…')}
              {...register('name')}
            />
          </Field>
          <Field label="Description" error={errors.description?.message}>
            <Input placeholder={t('optionnel', 'optional')} {...register('description')} />
          </Field>
          <Field label={t('Couleur', 'Color')} error={errors.color?.message}>
            <div className="flex flex-wrap gap-2">
              {PALETTE.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setValue('color', c)}
                  className="h-11 w-11 rounded-full border-2 transition-all sm:h-6 sm:w-6"
                  style={{
                    background: c,
                    borderColor: color === c ? 'hsl(var(--text-1))' : 'transparent',
                    boxShadow: color === c ? `0 0 10px ${c}` : undefined,
                  }}
                  aria-label={c}
                  aria-pressed={color === c}
                />
              ))}
            </div>
          </Field>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('Annuler', 'Cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={pending}>
              {pending && <Loader2 className="h-3 w-3 animate-spin" />}
              {editing ? t('Enregistrer', 'Save') : t('Créer', 'Create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  const { t } = useI18n();
  const errorLabels: Record<string, string> = {
    required: t('Requis', 'Required'),
    max64: t('Max 64 caractères', 'Maximum 64 characters'),
    max240: t('Max 240 caractères', 'Maximum 240 characters'),
    invalidColor: t('Couleur hex attendue (#RRGGBB)', 'Enter a hex color (#RRGGBB)'),
  };
  const errorLabel = error ? (errorLabels[error] ?? error) : undefined;
  return (
    <div className="space-y-1.5">
      <div className="text-[12px] font-medium text-text-2">{label}</div>
      {children}
      {errorLabel && <p className="text-[11px] text-rose">{errorLabel}</p>}
    </div>
  );
}
